import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { TASK_CATEGORIES, createTask, getTaskById, updateTask, deleteTask } from "@/lib/tasks";
import { toISODate, parseISODate, formatShortDate, todayISO } from "@/lib/time";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";

export default function CreateTask() {
  const { id } = useParams();
  const { t } = useTranslation();
  const existing = id ? getTaskById(id) : null;
  const isEdit = !!existing;
  const navigate = useNavigate();
  const [title, setTitle] = useState(existing?.title || "");
  const [description, setDescription] = useState(existing?.description || "");
  const [category, setCategory] = useState(existing?.category || "work");
  const [targetDate, setTargetDate] = useState(existing?.targetDate || todayISO());
  const [priority, setPriority] = useState(existing?.priority ?? 3);
  const [dateOpen, setDateOpen] = useState(false);

  const save = () => {
    if (!title.trim()) { toast.error(t("createTask.errAddTitle")); return; }
    const payload = { title: title.trim(), description: description.trim(), category, targetDate, priority: Number(priority) };
    if (isEdit) { updateTask(id, payload); toast.success(t("createTask.toastUpdated")); }
    else { createTask(payload); toast.success(t("createTask.toastSaved")); }
    navigate("/tasks");
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-2xl px-4 py-3 flex items-center gap-2">
          <button onClick={()=>navigate(-1)} className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="task-form-back"><ArrowLeft className="h-4 w-4" /></button>
          <div className="flex-1"><p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{isEdit?t("createTask.overlineEdit"):t("createTask.overlineNew")}</p><p className="font-display text-base font-semibold">{isEdit?t("createTask.headerEdit"):t("createTask.headerNew")}</p></div>
          <LanguageSwitcher />
        </div>
      </header>
      <main className="flex-1 mx-auto w-full max-w-2xl px-4 py-6 pb-28 space-y-5">
        <Field label={t("createTask.taskTitle")} required>
          <Input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder={t("createTask.taskTitlePlaceholder")} className="rounded-xl h-12" data-testid="task-title-input" />
        </Field>
        <Field label={t("createTask.description")}>
          <Textarea value={description} onChange={(e)=>setDescription(e.target.value)} rows={3} className="rounded-xl" data-testid="task-desc-input" />
        </Field>
        <Field label={t("createTask.category")}>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-xl h-12" data-testid="task-cat-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>{TASK_CATEGORIES.map((c)=> <SelectItem key={c.id} value={c.id} data-testid={`task-cat-${c.id}`}>{c.emoji} {t(`taskCategories.${c.id}`)}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("createTask.targetDate")}>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <button className="w-full text-left rounded-xl h-12 px-3.5 bg-white border border-input flex items-center justify-between" data-testid="task-date-trigger">
                <span className="text-sm">{formatShortDate(targetDate)}</span>
                <span className="text-xs font-mono-tab text-slate-500">{targetDate}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 w-auto rounded-2xl"><CalendarPicker mode="single" selected={parseISODate(targetDate)} onSelect={(d)=>{if(d){setTargetDate(toISODate(d)); setDateOpen(false);}}} initialFocus/></PopoverContent>
          </Popover>
        </Field>
        <Field label={t("createTask.priority")}>
          <Select value={String(priority)} onValueChange={(v)=>setPriority(Number(v))}>
            <SelectTrigger className="rounded-xl h-12" data-testid="task-prio-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>{[5,4,3,2,1].map((p)=> <SelectItem key={p} value={String(p)} data-testid={`task-prio-${p}`}>⭐{p}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </main>
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-border z-30">
        <div className="mx-auto max-w-2xl px-4 py-3 flex gap-3">
          {isEdit && <Button variant="outline" className="rounded-full h-12 px-4" onClick={()=>{deleteTask(id); toast.success(t("createTask.toastDeleted")); navigate("/tasks");}} data-testid="task-delete">{t("createTask.delete")}</Button>}
          <Button variant="outline" className="rounded-full h-12 flex-1 border-slate-300" onClick={()=>navigate(-1)} data-testid="task-cancel">{t("createTask.cancel")}</Button>
          <Button className="rounded-full h-12 flex-1 bg-slate-900" onClick={save} disabled={!title.trim()} data-testid="task-save">{isEdit?t("createTask.saveChanges"):t("createTask.saveTask")}</Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (<div className="space-y-1.5"><Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{label}{required?<span className="text-rose-500 ml-1">*</span>:null}</Label>{children}</div>);
}
